import { getStringOrDefault } from '@protector/utils/string';
import type { DateISOString } from '@protector/utils/types';
import moment from 'moment';
import { MAP_SEVERITY_COLORS, SEVERITY_COLORS_10 } from '../choropleth/constants.ts';
import type { Severity } from '../property/models.ts';
import type { CustomFormTimelineRecord, TimelineCustomFormRecord } from './models.ts';

export const toWindowCustomFormRecord = (record: CustomFormTimelineRecord, formTitle: string): TimelineCustomFormRecord => ({
  id: record.id,
  form_id: record.form_id,
  form_title: formTitle,
  form_version: record.form_version,
  field_id: record.field_id ?? null,
  reference_date: record.reference_date,
  location: record.location ?? null,
  values: record.values
});

export const getSeverityColorFromSeverity = (severity: Severity, useNewColors = false): string => {
  const colors = useNewColors ? MAP_SEVERITY_COLORS : SEVERITY_COLORS_10;
  switch (severity.label) {
    case 'ACCEPTANCE':
      return colors[0];
    case 'CONTROL':
      return colors[1];
    case 'DAMAGE':
      return colors[2];
    default:
      return 'transparent';
  }
};

export const createGroupWithDivisor = (max: number, min: number, count: number) => {
  const interval = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => Math.trunc(min + i * interval));
};

export const getColorFromPosition = (colorPosition: number, colorArray: string[], arrayPositions: number[]): string | null => {
  if (colorPosition >= arrayPositions[arrayPositions.length - 1]) {
    return colorArray[colorArray.length - 1];
  }

  const index = arrayPositions.findIndex((position, i) => {
    return colorPosition >= position && colorPosition < arrayPositions[i + 1];
  });

  return index !== -1 ? colorArray[index] : null;
};

export const getColorGeneralWithMaxValue = (
  dateString: DateISOString | null,
  colorArray: string[],
  maxValue: number,
  minValue = 0,
  minValueColor: string | null | undefined = null
): string => {
  if (dateString === null) {
    return 'transparent';
  }

  const date = moment(dateString).startOf('day');
  const now = moment().startOf('day');
  const daysDiff = now.diff(date, 'days');

  if (daysDiff < minValue) {
    return getStringOrDefault(minValueColor, '#FFF');
  }
  const arrayPositions = createGroupWithDivisor(maxValue, minValue, 6);
  const result = getColorFromPosition(daysDiff, colorArray, arrayPositions);
  return result ?? 'transparent';
};

export const normalizeSeverityLevel = (value?: number | null): number => value ?? 0;
